// src/components/layout/Header.js
import React, { useEffect, useRef, useState } from 'react';
import { Person, Search } from 'react-bootstrap-icons';
import { useLocation, useNavigate } from 'react-router-dom';

import { useBasePath } from 'utils/global';
import { attachHeaderListeners } from 'utils/headerListeners';
import axios from "axios";
const HEADLESS_CMS_API_URL = "/api/cms";
const SEARCH_TAGS_EXCLUDE = 'tags_exclude=313,314,645,655,664';
const SITE_PAGES = [
  { title: 'Home', path: '/' },
  { title: 'Solution', path: '/solution' },
  { title: 'Exposed Assets Discovery', path: '/solution/attack-surface-discover' },
  { title: 'Domain Spoofing Detection', path: '/solution/domain-spoofing-detection' },
  { title: 'Data Breach Detection', path: '/solution/data-breach-detection' },
  { title: 'Darknet Monitoring for MSSP', path: '/solution/darknet-monitoring-mssp' },
  { title: 'Pricing', path: '/pricing' },
  { title: 'Use Cases', path: '/use-cases' },
  { title: 'Use Case: CISO', path: '/use-cases/ciso' },
  { title: 'Use Case: Threat Intelligence', path: '/use-cases/threat' },
  { title: 'Use Case: SOC', path: '/use-cases/soc' },
  { title: 'Use Case: Finance Industry', path: '/use-cases/finance-industry' },
  { title: 'Use Case: Healthcare Industry', path: '/use-cases/healthcare-industry' },
  { title: 'Use Case: Ecommerce Industry', path: '/use-cases/ecommerce-industry' },
  { title: 'Use Case: Media Industry', path: '/use-cases/media-industry' },
  { title: 'Use Case: Government Industry', path: '/use-cases/government-industry' },
  { title: 'Use Case: Energy Industry', path: '/use-cases/energy-industry' },
  { title: 'Use Case: Technology Industry', path: '/use-cases/technology-industry' },
  { title: 'Use Case: Education Industry', path: '/use-cases/education-industry' },
  { title: 'Use Case: Legal Industry', path: '/use-cases/legal-industry' },
  { title: 'Use Case: Insurance Industry', path: '/use-cases/insurance-industry' },
  { title: 'Knowledge', path: '/knowledge' },
  { title: 'Glossary', path: '/glossary' },
  { title: 'Compare', path: '/compare' },
  { title: 'About Us', path: '/about-us' },
  { title: 'Free Trial', path: '/free-trial' },
  { title: 'Contact', path: '/contact' },
  { title: 'Documentation', path: '/documentation' },
  { title: 'Reports', path: '/reports' },
  { title: 'Sitemap', path: '/sitemap' },
  { title: 'Dark Web Monitoring', path: '/dark-web-monitoring' },
  { title: 'Credential Leak Detection', path: '/credential-leak-detection' },
  { title: 'Stealer Log Monitoring', path: '/stealer-log-monitoring' },
  { title: 'Ransomware Leak Monitoring', path: '/ransomware-leak-monitoring' },
  { title: 'Paste Site Monitoring', path: '/paste-site-monitoring' },
  { title: 'Botnet Infection Detection', path: '/botnet-infection-detection' },
  { title: 'Exposed Password Monitoring', path: '/exposed-password-monitoring' },
  { title: 'Email Compromise Monitoring', path: '/email-compromise-monitoring' },
  { title: 'What Is Dark Web', path: '/what-is-dark-web' },
  { title: 'Cyber Laws', path: '/cyber-laws' },
  { title: 'Privacy Policy', path: '/privacy-policy' },
  { title: 'Imprint', path: '/imprint' },
  { title: 'Terms of Service', path: '/tos' },
  { title: 'Calendly', path: '/calendly' },
  { title: 'Partners', path: '/partners' },
  { title: 'Customer Success Stories', path: '/customer-success-stories' },
  { title: 'Spain', path: '/es' },
  { title: 'France', path: '/fr' },
  { title: 'Germany', path: '/de' },
  { title: 'Login', path: '/login' },
  { title: 'Data Records', path: '/data-records' },
  { title: 'Breached Websites', path: '/breached-websites' },
  { title: 'Botnet Records', path: '/botnet-records' },
  { title: 'Compromised Bots', path: '/compromised-bots' },
  { title: 'Credit Card Leaks', path: '/credit-card-leaks' },
  { title: 'Pastebin Leaks', path: '/pastebin-leaks' },
];

const Header = () => {
  const routesForBackground = [
    '/knowledge',
    '/knowledge/news',
    '/pricing',
    '/use-cases',
    '/calendly',
    '/privacy-policy',
    '/imprint',
    '/tos',
    '/customer-success-stories',
    '/subscription-confirmed',
    '/frequently-asked-questions',
  ];
  const [announcementData, setAnnouncementData] = useState(null);
  const [isAnnouncement, setIsAnnounncementEnable] = useState(false);
  const basePath = useBasePath();
  const isBackground = routesForBackground.includes(basePath);
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [siteResults, setSiteResults] = useState([]);
  const [searchStatus, setSearchStatus] = useState('idle');
  const searchInputRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(routerLocation.search);
    const searchQuery = params.get('search') || '';
    setSearchTerm(searchQuery);
    setIsSearchOpen(Boolean(searchQuery));
  }, [routerLocation.search]);

  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  useEffect(() => {
    let isActive = true;
    const trimmed = searchTerm.trim();
    const normalized = trimmed.toLowerCase();
    const matchedPages = SITE_PAGES.filter((page) => {
      const haystack = `${page.title} ${page.path}`.toLowerCase();
      return haystack.includes(normalized);
    });
    setSiteResults(trimmed.length >= 2 ? matchedPages.slice(0, 8) : []);

    if (!isSearchOpen) {
      return () => {};
    }

    if (trimmed.length < 1) {
      setSearchResults([]);
      setSearchStatus('idle');
      return () => {};
    }

    setSearchStatus('loading');

    const timer = setTimeout(async () => {
      try {
        const response = await axios.get(
          `${HEADLESS_CMS_API_URL}/posts?${SEARCH_TAGS_EXCLUDE}`,
          {
            params: {
              status: 'publish',
              per_page: 6,
              search: trimmed,
            },
          }
        );

        if (!isActive) return;
        setSearchResults(response.data || []);
        setSearchStatus('done');
      } catch (error) {
        if (!isActive) return;
        setSearchResults([]);
        setSearchStatus('error');
      }
    }, 250);

    return () => {
      isActive = false;
      clearTimeout(timer);
    };
  }, [isSearchOpen, searchTerm]);

  useEffect(() => attachHeaderListeners(), []);

  useEffect(() => {
    async function fetchAnnouncement() {
      try {
        const res = await axios.get(`${HEADLESS_CMS_API_URL}/announcement`);
        // axios puts the response body in res.data
        setAnnouncementData(res.data);
        setIsAnnounncementEnable(res.data.enabled)
      } catch (err) {
        console.error("Failed to load announcement bar:", err);
      }
    }

    fetchAnnouncement();
  }, []);

  const submitSearch = () => {
    const trimmed = searchTerm.trim();
    if (!trimmed) {
      setIsSearchOpen(false);
      return;
    }

    const params = new URLSearchParams();
    const currentParams = new URLSearchParams(routerLocation.search);
    const currentCategory = currentParams.get('category');
    if (currentCategory) {
      params.set('category', currentCategory);
    }
    params.set('search', trimmed);
    navigate(`/knowledge?${params.toString()}`);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    submitSearch();
  };

  const handleSearchIconClick = () => {
    setIsSearchOpen(true);
  };

  const stripHtml = (html) => {
    if (!html) return '';
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return temp.textContent || temp.innerText || '';
  };
  const escapeRegExp = (value) =>
    value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const renderHighlighted = (text, query) => {
    if (!query) return text;
    const safeQuery = escapeRegExp(query);
    const regex = new RegExp(`(${safeQuery})`, 'gi');
    const testRegex = new RegExp(safeQuery, 'i');
    return text.split(regex).map((part, index) =>
      testRegex.test(part) ? (
        <span key={`${part}-${index}`} className="search-highlight">
          {part}
        </span>
      ) : (
        part
      )
    );
  };
  const fallbackThumb = require('../../assets/images/logo.png');
  return (
    <header
      className="container-fluid dog z-[90]"
      style={
        isBackground
          ? { paddingBottom: 10, backgroundColor: 'rgba(0, 0, 0, 0.8)' }
          : {}
      }
    >
      {
        isAnnouncement && 
        <div class="header-announment"
          style={
            { backgroundColor: announcementData.bg, color: announcementData.text_color }
          }
        >
          <div class="header-announcement-inner">
            <div>{announcementData.text} </div>
            <a href={announcementData.link_url}>{announcementData.button_name}</a>
          </div>
          
        </div>
      }
      <div className="container mx-auto pl-4 pr-8 lg:pl-4">
        <div className="row">
          <div className="cheader">
            <a title="" href="/">
              <img
                src={require('../../assets/images/logo.png')}
                className="img-fluid logo"
                alt="Darknetsearch"
              />
            </a>
            <div className="navigation">
              <ul>
                <li className="has-dropdown">
                  <a title="" href="/solution">
                    Solution
                  </a>
                  <ul className="submenu">
                    <li>
                      <a href="/solution/attack-surface-discover">
                        Exposed Assets Discovery
                      </a>
                    </li>
                    <li>
                      <a href="/solution/domain-spoofing-detection">Domain Spoofing Detection</a>
                    </li>
                    <li>
                      <a href="/solution/data-breach-detection">Data Breach Detection</a>
                    </li>
                    <li>
                      <a href="/solution/darknet-monitoring-mssp">Darknet Monitoring for MSSP</a>
                    </li>
                  </ul>
                </li>
                <li>
                  <a title="" href="/pricing">
                    Pricing
                  </a>
                </li>
                <li>
                  <a title="Use Cases" href="/use-cases">
                    Use Cases
                  </a>
                </li>
                <li>
                  <a title="" href="/knowledge">
                    Knowledge
                  </a>
                </li>
                <li>
                  <a title="" href="/about-us">
                    About us
                  </a>
                </li>
                <li className="nav-cta">
                  <a title="" href="/free-trial">
                    <div className="flex items-center">
                      <span className="me-2">
                        <Person />
                      </span>{' '}
                      FREE TRIAL
                    </div>
                  </a>
                </li>
                <li className="nav-search-item">
                  <button
                    type="button"
                    className="nav-search-toggle"
                    onClick={handleSearchIconClick}
                    aria-label="Open search"
                  >
                    <Search />
                    <span>Search</span>
                  </button>
                </li>
              </ul>
            </div>
            <div className="header-search">
              <button
                type="button"
                className="search-toggle"
                onClick={handleSearchIconClick}
                aria-label="Open search"
              >
                <Search />
              </button>
            </div>
            <div className="menu hamburger">
              <div className="icon"></div>
            </div>
          </div>
        </div>
      </div>
      {isSearchOpen && (
        <div className="header-search-overlay" role="dialog" aria-modal="true">
          <div
            className="search-backdrop"
            onClick={() => setIsSearchOpen(false)}
          ></div>
          <div className="search-panel">
            <button
              type="button"
              className="search-close"
              onClick={() => setIsSearchOpen(false)}
              aria-label="Close search"
            >
              ×
            </button>
            <form
              onSubmit={handleSearchSubmit}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  setIsSearchOpen(false);
                }
              }}
            >
              <Search className="search-icon" />
              <input
                ref={searchInputRef}
                type="search"
                placeholder="Search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                aria-label="Search"
              />
              <button type="submit" className="search-submit">
                Search
              </button>
            </form>
            <div className="search-results">
              {searchTerm.trim().length > 1 && (
                <div className="results-header">
                  Results for <span>{searchTerm.trim()}</span>
                </div>
              )}
              {searchStatus === 'loading' && (
                <div className="results-empty">Searching...</div>
              )}
              {searchStatus === 'done' &&
                siteResults.length === 0 &&
                searchResults.length === 0 && (
                <div className="results-empty">No results found.</div>
              )}
              {searchStatus === 'done' && (siteResults.length > 0 || searchResults.length > 0) && (
                <div className="results-list">
                  {siteResults.length > 0 && (
                    <>
                      <div className="results-section">Pages</div>
                      {siteResults.map((item) => (
                        <a
                          key={item.path}
                          href={item.path}
                          className="result-item"
                          onClick={() => setIsSearchOpen(false)}
                        >
                          <div className="result-thumb">
                            <img src={fallbackThumb} alt="" />
                          </div>
                          <div className="result-body">
                            <div className="result-title">
                              {renderHighlighted(item.title, searchTerm.trim())}
                            </div>
                            <div className="result-excerpt">
                              {renderHighlighted(item.path, searchTerm.trim())}
                            </div>
                          </div>
                        </a>
                      ))}
                    </>
                  )}
                  {searchResults.length > 0 && (
                    <>
                      <div className="results-section">Articles</div>
                      {searchResults.map((item) => (
                        <a
                          key={item.id}
                          href={`/knowledge/news/en/${item.slug}`}
                          className="result-item"
                          onClick={() => setIsSearchOpen(false)}
                        >
                          <div className="result-thumb">
                            <img
                              src={item.custom_thumbnail || fallbackThumb}
                              alt=""
                            />
                          </div>
                          <div className="result-body">
                            <div className="result-title">
                              {renderHighlighted(
                                stripHtml(item.title?.rendered),
                                searchTerm.trim()
                              )}
                            </div>
                            <div className="result-excerpt">
                              {renderHighlighted(
                                stripHtml(item.excerpt?.rendered),
                                searchTerm.trim()
                              )}
                            </div>
                          </div>
                        </a>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
